import React, { useState, useRef } from "react";
import {
  ArrowLeft, Phone, Navigation, Camera, FileText, Loader2,
  MapPin, KeyRound, Check, Download, ClipboardCheck, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import {
  TECH_STATUSES, STATUS_META, SERVICE_TYPES, REQUEST_TYPES, URGENCY_META, PHOTO_CATEGORIES, labelOf,
} from "../../lib/gmaoConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Card } from "../ui/card";
import { Separator } from "../ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import StatusBadge from "./StatusBadge";
import PhotoThumb from "./PhotoThumb";
import SignaturePad from "./SignaturePad";

const resizeImage = (file, maxDim = 1600, quality = 0.8) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
          else { width = Math.round((width * maxDim) / height); height = maxDim; }
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        c.toBlob((b) => resolve(b || file), "image/jpeg", quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

const emptyReport = {
  workDone: "", materialUsed: "", difficulty: "", partToOrder: "",
  clientObservation: "", completed: true, nextAction: "",
};

const TechnicianInterventionView = ({ intervention, onBack, onChanged }) => {
  const [iv, setIv] = useState(intervention);
  const [busy, setBusy] = useState(false);
  const [photoCategory, setPhotoCategory] = useState("avant");
  const [uploading, setUploading] = useState(false);
  const initReport = () => {
    const r = intervention.report || {};
    const merged = { ...emptyReport };
    Object.keys(emptyReport).forEach((k) => {
      if (k === "completed") merged.completed = r.completed ?? true;
      else merged[k] = r[k] ?? "";
    });
    return merged;
  };
  const [report, setReport] = useState(initReport);
  const [savingReport, setSavingReport] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [savingSig, setSavingSig] = useState(false);
  const fileRef = useRef(null);
  const sigRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await API.get(`/interventions/${iv.id}`);
      setIv(data);
      onChanged?.(data);
    } catch { /* ignore */ }
  };

  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${iv.address} ${iv.postalCode || ""} ${iv.city || ""}`
  )}`;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await API.patch(`/interventions/${iv.id}/status`, { status });
      setIv(data); onChanged?.(data);
      toast.success(`Statut : ${STATUS_META[status]?.label || status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const blob = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", blob, (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg");
      fd.append("category", photoCategory);
      await API.post(`/interventions/${iv.id}/photos`, fd);
      toast.success("Photo ajoutée");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const saveReport = async () => {
    setSavingReport(true);
    try {
      const { data } = await API.patch(`/interventions/${iv.id}/report`, report);
      setIv(data); onChanged?.(data);
      toast.success("Rapport enregistré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingReport(false); }
  };

  const saveSignature = async () => {
    if (sigRef.current?.isEmpty()) { toast.error("Veuillez faire signer le client."); return; }
    if (!signerName.trim()) { toast.error("Nom du signataire requis."); return; }
    setSavingSig(true);
    try {
      const dataUrl = sigRef.current.getDataURL();
      const { data } = await API.post(`/interventions/${iv.id}/signature`, { signatureDataUrl: dataUrl, signerName });
      setIv(data); onChanged?.(data);
      toast.success("Signature enregistrée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingSig(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await API.get(`/interventions/${iv.id}/report-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { toast.error("PDF indisponible."); }
  };

  const u = URGENCY_META[iv.urgency] || {};
  const photosByCat = (cat) => (iv.photos || []).filter((p) => p.category === cat);

  return (
    <div className="max-w-xl mx-auto pb-10" data-testid="technician-intervention-view">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 mb-3" data-testid="tech-back-button">
        <ArrowLeft className="w-4 h-4" /> Mes interventions
      </button>

      {/* En-tête */}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="font-mono font-bold text-[hsl(207,78%,42%)]">{iv.dossierNumber}</span>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded ${u.color || ""}`}>{u.label || iv.urgency}</span>
            <StatusBadge status={iv.status} />
          </div>
        </div>
        <p className="text-sm font-medium mt-2">{labelOf(REQUEST_TYPES, iv.requestType)} · {labelOf(SERVICE_TYPES, iv.serviceType)}</p>
        <p className="text-sm text-slate-600 mt-1">{iv.description}</p>
        {iv.preferredSlot && <p className="text-xs text-muted-foreground mt-1">Créneau : {iv.preferredSlot}</p>}
      </Card>

      {/* Actions rapides */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={(!iv.clientPhone && !iv.tenantPhone) ? "col-span-2" : ""}>
          <Button variant="outline" className="w-full h-12" data-testid="tech-gps"><Navigation className="w-4 h-4 mr-2" /> Itinéraire GPS</Button>
        </a>
        {iv.clientPhone ? (
          <a href={`tel:${iv.clientPhone}`}><Button variant="outline" className="w-full h-12"><Phone className="w-4 h-4 mr-2" /> Appeler client</Button></a>
        ) : iv.tenantPhone ? (
          <a href={`tel:${iv.tenantPhone}`}><Button variant="outline" className="w-full h-12"><Phone className="w-4 h-4 mr-2" /> Appeler locataire</Button></a>
        ) : null}
      </div>

      {/* Consignes d'accès */}
      <Card className="p-4 mb-4 space-y-1 text-sm">
        <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[hsl(207,78%,42%)]" /> {iv.address} {iv.postalCode} {iv.city}</p>
        {iv.floor && <p className="text-slate-600">Étage : {iv.floor}</p>}
        {iv.digicode && <p className="flex items-center gap-2 text-slate-600"><KeyRound className="w-4 h-4" /> Digicode : {iv.digicode}</p>}
        {iv.accessInfo && <p className="text-slate-600">Accès : {iv.accessInfo}</p>}
        {iv.tenantName && <p className="text-slate-600">Locataire : {iv.tenantName} {iv.tenantPhone}</p>}
      </Card>

      {/* Statuts */}
      <p className="font-semibold text-sm mb-2">Statut de l&apos;intervention</p>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {TECH_STATUSES.map((s) => (
          <Button
            key={s.value}
            variant={iv.status === s.value ? "default" : "outline"}
            className={`h-14 text-xs px-1 ${iv.status === s.value ? "btn-primary" : ""}`}
            disabled={busy}
            onClick={() => setStatus(s.value)}
            data-testid={`tech-status-${s.value}`}
          >
            {s.label}
          </Button>
        ))}
      </div>

      {/* Photos */}
      <Separator className="my-4" />
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-sm flex items-center gap-2"><Camera className="w-4 h-4" /> Photos</p>
      </div>
      <div className="flex gap-2 mb-3">
        <Select value={photoCategory} onValueChange={setPhotoCategory}>
          <SelectTrigger className="flex-1" data-testid="photo-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PHOTO_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickFile} data-testid="photo-file-input" />
        <Button className="btn-primary h-10" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="add-photo-button">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          Ajouter
        </Button>
      </div>
      {PHOTO_CATEGORIES.map((c) => {
        const list = photosByCat(c.value);
        if (list.length === 0) return null;
        return (
          <div key={c.value} className="mb-3">
            <p className="text-xs text-muted-foreground mb-1">{c.label}</p>
            <div className="grid grid-cols-3 gap-2">
              {list.map((p) => (
                <PhotoThumb key={p.id} interventionId={iv.id} photo={p} className="w-full h-24 rounded-lg border" />
              ))}
            </div>
          </div>
        );
      })}

      {/* Rapport */}
      <Separator className="my-4" />
      <p className="font-semibold text-sm flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> Rapport d&apos;intervention</p>
      <div className="space-y-3">
        <div><Label className="text-xs">Travail réalisé</Label><Textarea rows={2} value={report.workDone} onChange={(e) => setReport((r) => ({ ...r, workDone: e.target.value }))} data-testid="report-workdone" /></div>
        <div><Label className="text-xs">Matériel utilisé</Label><Input value={report.materialUsed} onChange={(e) => setReport((r) => ({ ...r, materialUsed: e.target.value }))} data-testid="report-material" /></div>
        <div><Label className="text-xs">Difficulté rencontrée</Label><Input value={report.difficulty} onChange={(e) => setReport((r) => ({ ...r, difficulty: e.target.value }))} /></div>
        <div><Label className="text-xs">Pièce à commander</Label><Input value={report.partToOrder} onChange={(e) => setReport((r) => ({ ...r, partToOrder: e.target.value }))} /></div>
        <div><Label className="text-xs">Observation client</Label><Input value={report.clientObservation} onChange={(e) => setReport((r) => ({ ...r, clientObservation: e.target.value }))} /></div>
        <div className="flex items-center justify-between border rounded-lg p-3">
          <span className="text-sm">Intervention terminée</span>
          <Switch checked={report.completed} onCheckedChange={(v) => setReport((r) => ({ ...r, completed: v }))} data-testid="report-completed" />
        </div>
        {!report.completed && (
          <div><Label className="text-xs">Prochaine action à prévoir</Label><Input value={report.nextAction} onChange={(e) => setReport((r) => ({ ...r, nextAction: e.target.value }))} /></div>
        )}
        <Button className="w-full btn-primary h-11" disabled={savingReport} onClick={saveReport} data-testid="save-report-button">
          {savingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
          Enregistrer le rapport
        </Button>
      </div>

      {/* Signature */}
      <Separator className="my-4" />
      <p className="font-semibold text-sm flex items-center gap-2 mb-2"><PenLine className="w-4 h-4" /> Signature client</p>
      {iv.signature ? (
        <Card className="p-3 bg-emerald-50 text-sm flex items-center gap-2" data-testid="signature-done">
          <Check className="w-4 h-4 text-emerald-600" />
          Signé par <strong>{iv.signature.signerName}</strong> le {(iv.signature.at || "").slice(0, 16).replace("T", " ")}
        </Card>
      ) : (
        <div className="space-y-2">
          <SignaturePad ref={sigRef} />
          <Input placeholder="Nom du signataire" value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="signer-name" />
          <Button className="w-full btn-primary h-11" disabled={savingSig} onClick={saveSignature} data-testid="save-signature-button">
            {savingSig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenLine className="w-4 h-4 mr-2" />}
            Enregistrer la signature
          </Button>
        </div>
      )}

      {/* Bon d'intervention PDF */}
      <Separator className="my-4" />
      <Button variant="outline" className="w-full h-11" onClick={downloadPdf} data-testid="tech-pdf-button">
        <Download className="w-4 h-4 mr-2" /> Bon d&apos;intervention (PDF)
      </Button>
    </div>
  );
};

export default TechnicianInterventionView;
